import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './ContactUs.css';
import receptionImage from '../components/logos/reception.avif';
import receptionImageDark from '../components/logos/receptionN.avif';
import SeoHead from '../seo/SeoHead';
import { buildContactPageSchema } from '../seo/siteSeo';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '';


const ContactUs = () => {
    const location = useLocation();
    const [formState, setFormState] = useState({
        fullName: '',
        workEmail: '',
        contactNumber: '',
        preferredDate: '',
        preferredTime: '',
        additionalNotes: '',
    });
    const [status, setStatus] = useState('');
    const [focusedField, setFocusedField] = useState('');
    const [errors, setErrors] = useState({
        fullName: '',
        workEmail: '',
        contactNumber: '',
    });
    const [notesLength, setNotesLength] = useState(0);
    const [touched, setTouched] = useState({});
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [windowWidth, setWindowWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1200
    );

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const messageParam = params.get('message') || location.state?.message;
        const planParam = params.get('plan') || location.state?.plan;

        let autoText = '';
        if (messageParam) {
            autoText = messageParam;
        } else if (planParam) {
            autoText = `I would like to enquire about the ${planParam} Plan for ElyConcierge.`;
        }

        if (autoText) {
            setFormState(prev => ({
                ...prev,
                additionalNotes: autoText
            }));
            setNotesLength(autoText.length);
        }
    }, [location]);

    useEffect(() => {
        const checkDark = () => {
            // Check for .dark-mode on body or html
            return document.body.classList.contains('dark-mode') || document.documentElement.classList.contains('dark-mode');
        };
        setIsDarkMode(checkDark());
        const observer = new MutationObserver(() => setIsDarkMode(checkDark()));
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState({ ...formState, [name]: value });

        if (name === 'fullName') {
            if (!/^[a-zA-Z\s]*$/.test(value)) {
                setErrors({ ...errors, fullName: 'Only letters and spaces allowed' });
            } else {
                setErrors({ ...errors, fullName: '' });
            }
        }
        if (name === 'workEmail') {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value) {
                setErrors({ ...errors, workEmail: 'Invalid email format' });
            } else {
                setErrors({ ...errors, workEmail: '' });
            }
        }
        if (name === 'contactNumber') {
            if (!/^\+?[\d\s-]{7,15}$/.test(value) && value) {
                setErrors({ ...errors, contactNumber: 'Enter 7-15 digits' });
            } else {
                setErrors({ ...errors, contactNumber: '' });
            }
        }
        if (name === 'additionalNotes') {
            setNotesLength(value.length);
        }
    };

    const handleFocus = (e) => {
        setFocusedField(e.target.name);
    };

    const handleBlur = (e) => {
        setFocusedField('');
        setTouched({ ...touched, [e.target.name]: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const requiredFields = ['fullName', 'workEmail', 'contactNumber', 'preferredDate', 'preferredTime'];
        const missingFields = requiredFields.filter(field => !formState[field]);

        if (missingFields.length > 0) {
            setStatus('error');
            setTimeout(() => setStatus(''), 3000);
            return;
        }

        if (errors.fullName || errors.workEmail || errors.contactNumber) {
            setStatus('error');
            setTimeout(() => setStatus(''), 3000);
            return;
        }

        if (!WEB3FORMS_ACCESS_KEY) {
            console.error('REACT_APP_WEB3FORMS_ACCESS_KEY is not configured');
            setStatus('error');
            setTimeout(() => setStatus(''), 3000);
            return;
        }

        setStatus('sending');

        try {
            const response = await fetch(WEB3FORMS_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    access_key: WEB3FORMS_ACCESS_KEY,
                    subject: `Book a Call Request — ${formState.fullName}`,
                    from_name: formState.fullName,
                    name: formState.fullName,
                    email: formState.workEmail,
                    phone: formState.contactNumber,
                    preferred_date: formState.preferredDate,
                    preferred_time: formState.preferredTime,
                    message: formState.additionalNotes || 'No additional notes provided.',
                    botcheck: '',
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Form submission failed');
            }

            setFormState({
                fullName: '',
                workEmail: '',
                contactNumber: '',
                preferredDate: '',
                preferredTime: '',
                additionalNotes: '',
            });
            setNotesLength(0);
            setStatus('success');
            setTimeout(() => setStatus(''), 3000);
        } catch (error) {
            console.error('Form submission error:', error);
            setStatus('error');
            setTimeout(() => setStatus(''), 5000);
        }
    };

    const timeOptions = [];
    const minutes = ['00', '15', '30', '45'];
    for (let hour = 9; hour <= 12; hour++) {
        for (let minute of minutes) {
            timeOptions.push(`${hour}:${minute} AM`);
        }
    }
    for (let hour = 1; hour <= 6; hour++) {
        for (let minute of minutes) {
            timeOptions.push(`${hour}:${minute} PM`);
        }
    }

    const today = new Date().toISOString().split('T')[0];

    return (
        <section id="contact" className="contact">
            <SeoHead pageKey="contact" schemas={[buildContactPageSchema()]} />
            <div className="section-header" style={{ marginTop: '1rem', marginBottom: '2.5rem' }}>
                <h2 data-text="Let’s Build the Future of Hospitality">Let’s Build the Future of Hospitality</h2>
            </div>
            <div className="contact-container" style={{ marginTop: '2.5rem', paddingLeft: '2rem', paddingRight: '2rem', maxWidth: '1200px', marginLeft: 'auto', marginRight: 'auto' }}>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: windowWidth <= 768 ? 'flex-end' : 'center',
                        marginBottom: '2rem',
                        padding: windowWidth <= 768 ? '0 12px' : '0'
                    }}
                >
                    <img
                        src={isDarkMode ? receptionImageDark : receptionImage}
                        alt="Reception"
                        style={{
                            width: '100%',
                            maxWidth: windowWidth <= 768 ? '600px' : '1035px',
                            height: 'auto',
                            borderRadius: '10px',
                            objectFit: 'cover',
                            boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
                            marginTop: windowWidth <= 768 ? '48px' : '100px'
                        }}
                    />
                </div>
                <div className="form-wrapper" style={{ marginLeft: '2rem' }}>
                    <form className="contact-form" onSubmit={handleSubmit} style={{ background: '#ffffff', boxShadow: 'none' }}>
                        <div className="form-header">
                            <h3>Book a Call</h3>
                            <p>Your personalized experience is one call away!</p>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <div className="input-wrapper">
                                    <i className="fas fa-user prefix-icon"></i>
                                    <label
                                        htmlFor="fullName"
                                        className={focusedField === 'fullName' || formState.fullName ? 'active' : ''}
                                    >
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        value={formState.fullName}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        required
                                    />
                                </div>
                                {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                            </div>
                            <div className="form-group">
                                <div className="input-wrapper">
                                    <i className="fas fa-envelope prefix-icon"></i>
                                    <label
                                        htmlFor="workEmail"
                                        className={focusedField === 'workEmail' || formState.workEmail ? 'active' : ''}
                                    >
                                        Work Email
                                    </label>
                                    <input
                                        type="email"
                                        id="workEmail"
                                        name="workEmail"
                                        value={formState.workEmail}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        required
                                    />
                                </div>
                                {errors.workEmail && <span className="error-message">{errors.workEmail}</span>}
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <div className="input-wrapper">
                                    <i className="fas fa-phone prefix-icon"></i>
                                    <label
                                        htmlFor="contactNumber"
                                        className={focusedField === 'contactNumber' || formState.contactNumber ? 'active' : ''}
                                    >
                                        Contact Number
                                    </label>
                                    <input
                                        type="tel"
                                        id="contactNumber"
                                        name="contactNumber"
                                        value={formState.contactNumber}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        required
                                    />
                                </div>
                                {errors.contactNumber && <span className="error-message">{errors.contactNumber}</span>}
                            </div>
                            <div className="form-group">
                                <div className="input-wrapper">
                                    <i className="fas fa-calendar-alt prefix-icon"></i>
                                    <label
                                        htmlFor="preferredDate"
                                        className={focusedField === 'preferredDate' || formState.preferredDate ? 'active' : ''}
                                    >
                                        Preferred Date
                                    </label>
                                    <input
                                        type="date"
                                        id="preferredDate"
                                        name="preferredDate"
                                        value={formState.preferredDate}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        min={today}
                                        required
                                        className="date-input"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="form-group">
                            <div className="input-wrapper">
                                <i className="fas fa-clock prefix-icon"></i>
                                <label
                                    htmlFor="preferredTime"
                                    className={focusedField === 'preferredTime' || formState.preferredTime ? 'active' : ''}
                                >
                                    Preferred Time
                                </label>
                                <select
                                    id="preferredTime"
                                    name="preferredTime"
                                    value={formState.preferredTime}
                                    onChange={handleChange}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    className="time-select"
                                    required
                                >
                                    <option value="" disabled>
                                         
                                    </option>
                                    {timeOptions.map((time) => (
                                        <option key={time} value={time}>
                                            {time}
                                        </option>
                                    ))}
                                </select>
                                <span className="tooltip">Available from 9:00 AM to 6:00 PM</span>
                            </div>
                        </div>
                        <div className="form-group">
                            <div className="input-wrapper">
                                <i className="fas fa-comment prefix-icon"></i>
                                <label
                                    htmlFor="additionalNotes"
                                    className={focusedField === 'additionalNotes' || formState.additionalNotes ? 'active' : ''}
                                >
                                    Additional Notes (Optional)
                                </label>
                                <textarea
                                    id="additionalNotes"
                                    name="additionalNotes"
                                    value={formState.additionalNotes}
                                    onChange={handleChange}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    maxLength="500"
                                ></textarea>
                            </div>
                            <span className="char-counter">{notesLength}/500 characters</span>
                        </div>
                        <button
                            type="submit"
                            disabled={status === 'sending' || Object.values(errors).some((e) => e) || !formState.fullName || !formState.workEmail || !formState.contactNumber || !formState.preferredDate || !formState.preferredTime}
                            className={status === 'sending' ? 'disabled' : ''}
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                            {status === 'sending' ? (
                                <span>
                                    Scheduling...
                                </span>
                            ) : status === 'success' ? (
                                <span>
                                    Demo Scheduled!
                                </span>
                            ) : status === 'error' ? (
                                <span>
                                    Error Scheduling
                                </span>
                            ) : (
                                <>
                                    <span>Schedule Call</span>
                                </>
                            )}
                        </button>
                        {status && (
                            <div className={`status-message ${status}`}>
                                {status === 'success' ? (
                                    <span>
                                        <i className="fas fa-check-circle"></i> We'll send you a confirmation soon!
                                    </span>
                                ) : status === 'error' ? (
                                    <span>
                                        <i className="fas fa-exclamation-circle"></i> Please try again later.
                                    </span>
                                ) : (
                                    ''
                                )}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default ContactUs; 